import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { List, Loader2, Map as MapIcon, MapPin, MapPinned, Pencil, Plus, Save, Trash2, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'
import { apiClient } from '@/api/client'
import { loadGoogleMaps, onMapsAuthFailure } from '@/lib/googleMaps'
import { useMapsConfig } from '@/hooks/useMapsConfig'
import { PlaceSearch } from '@/components/maps/PlaceSearch'
import type { ResolvedPlace } from '@/lib/maps/types'
import {
  DeliveryZone,
  LatLng,
  ZonePolygon,
  drawnZonePolygon,
  newDeliveryZone,
  nextZoneColor,
  normalizeDeliveryZones,
  zoneRings,
  ZONE_COLORS,
} from '@/lib/deliveryZones'
import { AreaLibrary, AreaLibraryEntry, loadAreaLibrary, searchAreas } from '@/lib/areaLibrary'

/**
 * Delivery Zones editor (F-FE-N26 + F-FE-N29): each zone is a set of coverage
 * areas — picked by name from the predefined library (Islamabad/Rawalpindi,
 * OSM-derived) and/or drawn by hand — with a delivery charge, minimum order,
 * rider payout, and ETA. Zones apply to the online-ordering storefront only
 * (counter keeps its manual delivery-fee override). The whole list saves to
 * app_settings.delivery_zones; the storefront requires an in-zone pinned
 * location once at least one enabled zone exists.
 *
 * Manual drawing (Google removed DrawingManager in Maps JS v3.65): the draft
 * renders as a polyline + vertex dots from the very first click, every draft
 * artifact is non-clickable so it never swallows the next map click, and the
 * managed zone polygons are made non-clickable while drawing for the same
 * reason. Finish by clicking the first vertex, double-clicking, or the button.
 */

// Islamabad — a sane default viewport for a first-time setup before any
// zones exist. The Places search box moves the map anywhere.
const DEFAULT_CENTER: LatLng = { lat: 33.6844, lng: 73.0479 }

/** Two map clicks closer than this (≈1 m) are the same vertex (dblclick echo). */
const DUP_VERTEX_EPS = 1e-5

const round6 = (n: number) => Math.round(n * 1e6) / 1e6

function dedupeRing(ring: LatLng[]): LatLng[] {
  const near = (a: LatLng, b: LatLng) =>
    Math.abs(a.lat - b.lat) <= DUP_VERTEX_EPS && Math.abs(a.lng - b.lng) <= DUP_VERTEX_EPS
  const out: LatLng[] = []
  for (const p of ring) {
    if (out.length === 0 || !near(out[out.length - 1], p)) out.push(p)
  }
  // A closing click on the first vertex leaves a trailing duplicate of it.
  while (out.length > 1 && near(out[0], out[out.length - 1])) out.pop()
  return out
}

async function fetchSetting<T>(key: string, fallback: T): Promise<T> {
  try {
    const res = await apiClient.getSetting(key)
    return res.success && res.data != null ? (res.data as T) : fallback
  } catch {
    return fallback // 404 = never configured
  }
}

export function AdminDeliveryZones() {
  const { toast } = useToast()
  const queryClient = useQueryClient()

  // Maps keys are Bhookly's (F-BE-N90) — nothing for the restaurant to set up.
  const { data: maps, isLoading: keyLoading } = useMapsConfig()

  const { data: savedZones, isLoading: zonesLoading } = useQuery({
    queryKey: ['settings', 'delivery_zones'],
    queryFn: async () => normalizeDeliveryZones(await fetchSetting<unknown>('delivery_zones', [])),
  })

  const saveZonesMutation = useMutation({
    mutationFn: (zones: DeliveryZone[]) => apiClient.updateSetting('delivery_zones', zones),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'delivery_zones'] })
      toast({ title: 'Delivery zones saved' })
    },
    onError: (e: unknown) =>
      toast({
        title: 'Save failed',
        description: e instanceof Error ? e.message : 'Could not save delivery zones',
        variant: 'destructive',
      }),
  })

  if (keyLoading || zonesLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!maps?.browser_key) {
    return <MapsNotEnabled />
  }

  return (
    <ZonesEditor
      apiKey={maps.browser_key}
      search={maps.search}
      initialZones={savedZones ?? []}
      saving={saveZonesMutation.isPending}
      onSave={(zones) => saveZonesMutation.mutateAsync(zones)}
    />
  )
}

/**
 * Maps aren't switched on for this deployment yet. Bhookly issues the Google
 * keys (F-BE-N90), so there is nothing to paste — the owner contacts us.
 */
function MapsNotEnabled() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" /> Delivery zones need maps switched on
          </CardTitle>
          <CardDescription>
            Bhookly provides the map for your delivery zones and storefront — there is no Google account or key for you to
            set up. Maps aren&rsquo;t switched on for your restaurant yet: message Bhookly support and we&rsquo;ll turn
            them on.
          </CardDescription>
        </CardHeader>
      </Card>
    </div>
  )
}

// ── Map editor ───────────────────────────────────────────────────────────────

type ZonesEditorProps = {
  apiKey: string
  /** Server-side address search is available. */
  search: boolean
  initialZones: DeliveryZone[]
  saving: boolean
  // Resolves when the server accepted the zones; the mutation toasts failures.
  onSave: (zones: DeliveryZone[]) => Promise<unknown>
}

function ZonesEditor({ apiKey, search, initialZones, saving, onSave }: ZonesEditorProps) {
  const { toast } = useToast()
  const [zones, setZones] = useState<DeliveryZone[]>(initialZones)
  const [dirty, setDirty] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Draw mode: 'off' | new-zone | append-to-zone-id
  const [drawTarget, setDrawTarget] = useState<'new' | string | null>(null)
  const drawing = drawTarget != null
  const [mapsError, setMapsError] = useState('')
  const [mapsReady, setMapsReady] = useState(false)
  const [keyRejected, setKeyRejected] = useState(false)
  // Once Google rejects the key it tears the Maps objects down, so any later
  // polygon sync (every keystroke in a zone's fields re-runs it) would throw
  // and blank the whole page. Drop the map: every map path checks mapRef /
  // mapsReady, while charges, minimums and times stay editable.
  useEffect(
    () =>
      onMapsAuthFailure(() => {
        setKeyRejected(true)
        setMapsReady(false)
        mapRef.current = null
        polysRef.current.clear()
      }),
    [],
  )
  // Below md the two-pane split doesn't fit — a 416px list alone overflows a
  // 390px phone. Both panes stay mounted (the Maps instance is bound to its
  // div); we only toggle visibility with CSS so the map never re-initializes.
  const [mobilePane, setMobilePane] = useState<'list' | 'map'>('list')

  // Coming out of display:none the map has a stale size — kick it and re-center.
  useEffect(() => {
    if (mobilePane !== 'map' || !mapRef.current) return
    const map = mapRef.current
    const center = map.getCenter()
    google.maps.event.trigger(map, 'resize')
    if (center) map.setCenter(center)
  }, [mobilePane])

  const mapDivRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<google.maps.Map | null>(null)
  // One managed google Polygon per zone ring, keyed `${zoneId}:${ringIndex}`.
  const polysRef = useRef<Map<string, google.maps.Polygon>>(new Map())
  // In-progress draft: path is the source of truth; the polyline / preview
  // polygon / vertex markers re-render from it after every click. Everything
  // is non-clickable (except the closing first-vertex marker) so draft
  // artifacts never swallow the next map click.
  const draftPathRef = useRef<LatLng[]>([])
  const draftPolylineRef = useRef<google.maps.Polyline | null>(null)
  const draftPolygonRef = useRef<google.maps.Polygon | null>(null)
  const draftMarkersRef = useRef<google.maps.Marker[]>([])
  const draftColorRef = useRef<string>(ZONE_COLORS[0])
  // Refs so once-bound map listeners always see current state.
  const zonesRef = useRef(zones)
  zonesRef.current = zones
  const drawTargetRef = useRef(drawTarget)
  drawTargetRef.current = drawTarget

  const updateZone = (id: string, patch: Partial<DeliveryZone>) => {
    setZones((zs) => zs.map((z) => (z.id === id ? { ...z, ...patch } : z)))
    setDirty(true)
  }

  const clearDraft = () => {
    draftPathRef.current = []
    draftPolylineRef.current?.setMap(null)
    draftPolylineRef.current = null
    draftPolygonRef.current?.setMap(null)
    draftPolygonRef.current = null
    draftMarkersRef.current.forEach((m) => m.setMap(null))
    draftMarkersRef.current = []
  }

  /** Re-render the draft artifacts from draftPathRef. */
  const renderDraft = () => {
    const map = mapRef.current
    if (!map) return
    const path = draftPathRef.current
    const color = draftColorRef.current

    if (!draftPolylineRef.current) {
      draftPolylineRef.current = new google.maps.Polyline({
        map,
        path,
        clickable: false,
        strokeColor: color,
        strokeWeight: 3,
      })
    } else {
      draftPolylineRef.current.setOptions({ strokeColor: color })
      draftPolylineRef.current.setPath(path)
    }

    if (path.length >= 3) {
      if (!draftPolygonRef.current) {
        draftPolygonRef.current = new google.maps.Polygon({
          map,
          paths: path,
          clickable: false,
          strokeWeight: 0,
          fillColor: color,
          fillOpacity: 0.15,
        })
      } else {
        draftPolygonRef.current.setOptions({ fillColor: color })
        draftPolygonRef.current.setPath(path)
      }
    } else {
      draftPolygonRef.current?.setMap(null)
      draftPolygonRef.current = null
    }

    // Vertex dots. Only the FIRST vertex is clickable — clicking it closes
    // the ring (once there are enough points).
    while (draftMarkersRef.current.length > path.length) {
      draftMarkersRef.current.pop()?.setMap(null)
    }
    path.forEach((p, i) => {
      const isFirst = i === 0
      let marker = draftMarkersRef.current[i]
      if (!marker) {
        marker = new google.maps.Marker({
          map,
          position: p,
          clickable: isFirst,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: isFirst ? 7 : 5,
            fillColor: '#ffffff',
            fillOpacity: 1,
            strokeColor: color,
            strokeWeight: 2,
          },
          title: isFirst ? 'Click to close the area' : undefined,
        })
        if (isFirst) {
          marker.addListener('click', () => finishDrawingRef.current())
        }
        draftMarkersRef.current[i] = marker
      } else {
        marker.setPosition(p)
      }
    })
  }

  // Address search (server-side, F-BE-N90): jump the viewport to the place.
  function jumpTo(place: ResolvedPlace) {
    const map = mapRef.current
    if (!map) return
    const v = place.viewport
    if (v) map.fitBounds({ south: v.south, west: v.west, north: v.north, east: v.east })
    else {
      map.setCenter(place.location)
      map.setZoom(14)
    }
  }

  // ── Maps bootstrap ──
  useEffect(() => {
    let cancelled = false
    const listeners: google.maps.MapsEventListener[] = []
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !mapDivRef.current) return
        const map = new maps.Map(mapDivRef.current, {
          center: DEFAULT_CENTER,
          zoom: 12,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        })
        mapRef.current = map

        listeners.push(
          map.addListener('click', (e: google.maps.MapMouseEvent) => {
            if (!drawTargetRef.current || !e.latLng) return
            draftPathRef.current = [...draftPathRef.current, { lat: e.latLng.lat(), lng: e.latLng.lng() }]
            renderDraft()
          }),
          map.addListener('dblclick', () => {
            if (drawTargetRef.current) finishDrawingRef.current()
          }),
        )

        // Initial viewport: fit existing zones.
        const initial = zonesRef.current.flatMap((z) => zoneRings(z).flat())
        if (initial.length > 0) {
          const bounds = new maps.LatLngBounds()
          initial.forEach((p) => bounds.extend(p))
          map.fitBounds(bounds)
        }
        setMapsReady(true)
      })
      .catch((e: Error) => !cancelled && setMapsError(e.message))
    return () => {
      cancelled = true
      listeners.forEach((l) => l.remove())
      clearDraft()
      previewPolyRef.current?.setMap(null)
      previewPolyRef.current = null
      for (const poly of polysRef.current.values()) poly.setMap(null)
      polysRef.current.clear()
      mapRef.current = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey])

  // ── State → map sync: managed polygons (one per zone ring) ──
  useEffect(() => {
    if (!mapsReady || !mapRef.current) return
    const map = mapRef.current
    const polys = polysRef.current

    const wanted = new Set<string>()
    for (const zone of zones) {
      zone.polygons.forEach((_, i) => wanted.add(`${zone.id}:${i}`))
    }
    for (const [key, poly] of polys) {
      if (!wanted.has(key)) {
        poly.setMap(null)
        polys.delete(key)
      }
    }

    // Vertex edits on drawn rings flow back into state. setPath() swaps the
    // underlying MVCArray, so listeners must be (re)bound to the CURRENT path
    // each time it's replaced.
    const bindPathListeners = (poly: google.maps.Polygon, zoneId: string, ringIdx: number) => {
      const commitPath = () => {
        const ring = poly.getPath().getArray().map((p) => ({ lat: p.lat(), lng: p.lng() }))
        setZones((zs) =>
          zs.map((z) =>
            z.id === zoneId
              ? { ...z, polygons: z.polygons.map((pp, i) => (i === ringIdx ? { ...pp, ring } : pp)) }
              : z,
          ),
        )
        setDirty(true)
      }
      const path = poly.getPath()
      path.addListener('set_at', commitPath)
      path.addListener('insert_at', commitPath)
      path.addListener('remove_at', commitPath)
    }

    for (const zone of zones) {
      const isSelected = zone.id === selectedId
      zone.polygons.forEach((zp, ringIdx) => {
        const key = `${zone.id}:${ringIdx}`
        let poly = polys.get(key)
        if (!poly) {
          poly = new google.maps.Polygon({ map, paths: zp.ring })
          polys.set(key, poly)
          poly.addListener('click', () => {
            if (!drawTargetRef.current) setSelectedId(zone.id)
          })
          bindPathListeners(poly, zone.id, ringIdx)
        }
        // Only push the path when it differs — resetting it re-fires listeners.
        const current = poly.getPath().getArray()
        const changed =
          current.length !== zp.ring.length ||
          current.some((p, i) => p.lat() !== zp.ring[i].lat || p.lng() !== zp.ring[i].lng)
        if (changed) {
          poly.setPath(zp.ring)
          bindPathListeners(poly, zone.id, ringIdx)
        }
        poly.setOptions({
          fillColor: zone.color,
          strokeColor: zone.color,
          fillOpacity: zone.enabled ? (isSelected ? 0.4 : 0.22) : 0.08,
          strokeOpacity: zone.enabled ? 1 : 0.4,
          strokeWeight: isSelected ? 3 : 2,
          // Library rings are never hand-editable (remove the chip and draw
          // instead); drawn rings edit when their zone is selected. Everything
          // is non-clickable while drawing so clicks reach the map.
          editable: isSelected && zp.source === 'drawn' && !drawing,
          clickable: !drawing,
        })
      })
    }
  }, [zones, selectedId, mapsReady, drawing])

  const setDrawCursor = (on: boolean) => {
    mapRef.current?.setOptions({
      draggableCursor: on ? 'crosshair' : undefined,
      disableDoubleClickZoom: on,
    })
  }

  /** Blink-style entry point: a zone starts EMPTY — you then pick named areas
   *  from the dropdown on its card (or draw). No drawing required. */
  const addZone = () => {
    const zone = newDeliveryZone(zonesRef.current, [])
    setZones((zs) => [...zs, zone])
    setSelectedId(zone.id)
    setDirty(true)
  }

  /** target: 'new' = finished ring becomes a new zone; zone id = appended to it. */
  const startDrawing = (target: 'new' | string) => {
    if (!mapRef.current) return
    const targetZone = target === 'new' ? null : zonesRef.current.find((z) => z.id === target)
    draftColorRef.current = targetZone ? targetZone.color : nextZoneColor(zonesRef.current)
    setDrawTarget(target)
    setDrawCursor(true)
    setMobilePane('map') // drawing happens on the map — bring it forward on phones
  }

  const finishDrawing = () => {
    const target = drawTargetRef.current
    const path = dedupeRing(draftPathRef.current)
    clearDraft()
    setDrawTarget(null)
    setDrawCursor(false)
    if (!target) return
    if (path.length < 3) {
      toast({ title: 'Area needs at least 3 points', description: 'Click the map to outline it, then click the first dot to close.' })
      return
    }
    if (target === 'new') {
      const zone = newDeliveryZone(zonesRef.current, [drawnZonePolygon(null, path)])
      setZones((zs) => [...zs, zone])
      setSelectedId(zone.id)
    } else {
      const zone = zonesRef.current.find((z) => z.id === target)
      if (!zone) return
      const zp = drawnZonePolygon(zone, path)
      updateZone(zone.id, {
        polygons: [...zone.polygons, zp],
        areas: mergeAreaLabel(zone.areas, zp.name),
      })
      setSelectedId(zone.id)
    }
    setDirty(true)
  }
  // Stable handle for map/marker listeners (bound once at bootstrap).
  const finishDrawingRef = useRef(finishDrawing)
  finishDrawingRef.current = finishDrawing

  const cancelDrawing = () => {
    clearDraft()
    setDrawTarget(null)
    setDrawCursor(false)
  }

  const deleteZone = (id: string) => {
    setZones((zs) => zs.filter((z) => z.id !== id).map((z, i) => ({ ...z, sort_order: i })))
    if (selectedId === id) setSelectedId(null)
    setDirty(true)
  }

  const fitToRings = (rings: LatLng[][]) => {
    const pts = rings.flat()
    const map = mapRef.current
    if (!map || pts.length === 0) return
    const bounds = new google.maps.LatLngBounds()
    pts.forEach((p) => bounds.extend(p))
    map.fitBounds(bounds, 48)
  }

  // Dashed outline shown while hovering an option in the area dropdown, so
  // the operator sees the boundary before picking it.
  const previewPolyRef = useRef<google.maps.Polygon | null>(null)
  const previewArea = (entry: AreaLibraryEntry | null) => {
    previewPolyRef.current?.setMap(null)
    previewPolyRef.current = null
    const map = mapRef.current
    if (!entry || !map) return
    previewPolyRef.current = new google.maps.Polygon({
      map,
      paths: entry.ring,
      clickable: false,
      fillOpacity: 0.08,
      fillColor: '#64748b',
      strokeColor: '#334155',
      strokeWeight: 2,
      strokeOpacity: 0.9,
    })
    fitToRings([entry.ring])
  }

  const addLibraryArea = (zone: DeliveryZone, entry: AreaLibraryEntry) => {
    previewArea(null)
    if (zone.polygons.some((p) => p.slug === entry.slug)) return // already in this zone
    const elsewhere = zonesRef.current.find((z) => z.id !== zone.id && z.polygons.some((p) => p.slug === entry.slug))
    if (elsewhere) {
      toast({
        title: `${entry.name} is also in "${elsewhere.name}"`,
        description: 'Overlapping zones are allowed — the smaller total coverage wins at checkout.',
      })
    }
    const zp: ZonePolygon = { slug: entry.slug, name: entry.name, source: 'library', ring: entry.ring }
    updateZone(zone.id, {
      polygons: [...zone.polygons, zp],
      areas: mergeAreaLabel(zone.areas, entry.name),
    })
    setSelectedId(zone.id)
    fitToRings([entry.ring])
  }

  const removeZonePolygon = (zone: DeliveryZone, ringIdx: number) => {
    const removed = zone.polygons[ringIdx]
    updateZone(zone.id, {
      polygons: zone.polygons.filter((_, i) => i !== ringIdx),
      areas: zone.areas.filter((a) => a !== removed.name),
    })
  }

  const save = () => {
    const bad = zones.find((z) => !z.name.trim() || zoneRings(z).length === 0)
    if (bad) {
      toast({
        title: 'Fix before saving',
        description: `Zone "${bad.name || '(unnamed)'}" needs a name and at least one coverage area.`,
        variant: 'destructive',
      })
      return
    }
    onSave(
      zones.map((z, i) => ({
        ...z,
        name: z.name.trim(),
        sort_order: i,
        polygons: z.polygons.map((p) => ({
          ...p,
          ring: p.ring.map((pt) => ({ lat: round6(pt.lat), lng: round6(pt.lng) })),
        })),
      })),
    )
      // Only a save the server accepted clears "unsaved" — a rejected one
      // (e.g. too many vertices) must leave Save enabled to retry.
      .then(() => setDirty(false))
      .catch(() => {})
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-col gap-3 border-b px-4 py-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <h1 className="text-lg font-semibold">Delivery Zones</h1>
          <p className="hidden text-sm text-muted-foreground sm:block">
            Pick the areas you deliver to from the list — or draw them — then set each zone's
            delivery charge, minimum order, rider payout, and delivery time for online orders.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {drawing ? (
            <Button variant="outline" onClick={cancelDrawing}>
              <X className="mr-2 h-4 w-4" /> Cancel drawing
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => startDrawing('new')} disabled={!mapsReady}>
                <Pencil className="mr-2 h-4 w-4" /> Draw new zone
              </Button>
              <Button variant="secondary" onClick={addZone}>
                <Plus className="mr-2 h-4 w-4" /> Add zone
              </Button>
            </>
          )}
          <Button onClick={save} disabled={!dirty || saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
            Save changes
          </Button>
        </div>
      </div>

      {/* Phone: the split can't fit — segmented List / Map toggle instead */}
      <div className="grid shrink-0 grid-cols-2 gap-1 border-b bg-muted/30 p-1 md:hidden" role="tablist" aria-label="Zones view">
        <button
          type="button"
          role="tab"
          aria-selected={mobilePane === 'list'}
          onClick={() => setMobilePane('list')}
          className={cn(
            'flex min-h-11 items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors',
            mobilePane === 'list' ? 'bg-background shadow-sm' : 'text-muted-foreground'
          )}
        >
          <List className="h-4 w-4" /> Zones{zones.length > 0 ? ` (${zones.length})` : ''}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mobilePane === 'map'}
          onClick={() => setMobilePane('map')}
          className={cn(
            'flex min-h-11 items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors',
            mobilePane === 'map' ? 'bg-background shadow-sm' : 'text-muted-foreground'
          )}
        >
          <MapIcon className="h-4 w-4" /> Map
        </button>
      </div>

      <div className="flex min-h-0 flex-1">
        {/* Zone list */}
        <div
          className={cn(
            'w-full shrink-0 space-y-3 overflow-y-auto p-4 md:block md:w-80 md:border-r lg:w-[26rem]',
            mobilePane === 'map' && 'hidden'
          )}
        >
          {zones.length === 0 && (
            <Card>
              <CardContent className="pt-6 text-sm text-muted-foreground">
                No zones yet. Click <strong>Add zone</strong>, then pick the areas it covers from
                the <strong>Add area</strong> list (F-10, E-11, DHA Phase 2, …) — or draw custom
                shapes on the map for areas not in the list.
              </CardContent>
            </Card>
          )}
          {zones.map((zone) => (
            <ZoneCard
              key={zone.id}
              zone={zone}
              selected={zone.id === selectedId}
              drawing={drawing}
              onSelect={() => {
                setSelectedId(zone.id)
                fitToRings(zoneRings(zone))
              }}
              onChange={(patch) => updateZone(zone.id, patch)}
              onDelete={() => deleteZone(zone.id)}
              onAddLibraryArea={(entry) => addLibraryArea(zone, entry)}
              onRemovePolygon={(ringIdx) => removeZonePolygon(zone, ringIdx)}
              onDrawArea={() => startDrawing(zone.id)}
              onPreviewArea={previewArea}
            />
          ))}
          {dirty && (
            <p className="px-1 text-xs text-amber-600 dark:text-amber-500">
              Unsaved changes — customers still see the last saved zones.
            </p>
          )}
        </div>

        {/* Map */}
        <div className={cn('relative min-w-0 flex-1', mobilePane === 'list' && 'hidden md:block')}>
          <div ref={mapDivRef} className="h-full w-full" />
          {search && mapsReady && (
            <PlaceSearch
              className="absolute left-3 top-3 z-10 w-[min(18rem,calc(100%-1.5rem))] rounded-md bg-background shadow-md"
              near={() => {
                const c = mapRef.current?.getCenter()
                return c ? { lat: c.lat(), lng: c.lng() } : null
              }}
              onPick={jumpTo}
            />
          )}
          {drawing && (
            <div className="absolute bottom-4 left-1/2 flex w-[calc(100%-1rem)] max-w-xl -translate-x-1/2 flex-wrap items-center justify-center gap-3 rounded-md bg-background/95 px-3 py-2 text-sm shadow-md sm:w-auto">
              <span>
                Click the map to outline{' '}
                {drawTarget === 'new' ? 'the new zone' : `an area for "${zones.find((z) => z.id === drawTarget)?.name ?? ''}"`}
                . Close it via the first dot, a double-click, or:
              </span>
              <Button size="sm" onClick={finishDrawing}>
                Finish area
              </Button>
            </div>
          )}
          {/* Predefined areas are OSM-derived (ODbL) — attribution required. */}
          <div className="absolute bottom-6 right-1 rounded bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground">
            Area boundaries © OpenStreetMap contributors
          </div>
          {(mapsError || keyRejected) && (
            <div className="absolute inset-0 flex items-center justify-center bg-background p-6">
              {keyRejected ? <RejectedKeyNotice /> : <p className="text-center text-sm text-destructive">{mapsError}</p>}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * Google refused the key this page loaded with. The storefront hits the same
 * refusal, so customers order from the area list with no pin for the rider.
 * Keys are Bhookly's to fix (F-BE-N90) — nothing for the owner to paste.
 */
function RejectedKeyNotice() {
  return (
    <div className="max-w-md space-y-2 text-sm">
      <p className="font-medium text-destructive">The map isn&rsquo;t loading right now.</p>
      <p className="text-muted-foreground">
        Customers can still order delivery — they pick their area from a list — but there is no map pin for the rider.
        Message Bhookly support and we&rsquo;ll fix it; your zones, charges and times are safe and still editable.
      </p>
    </div>
  )
}

/** Case-insensitive append of a coverage label to the customer-facing list. */
function mergeAreaLabel(areas: string[], name: string): string[] {
  if (!name || areas.some((a) => a.toLowerCase() === name.toLowerCase())) return areas
  return [...areas, name].slice(0, 30)
}

// ── Zone card ────────────────────────────────────────────────────────────────

type ZoneCardProps = {
  zone: DeliveryZone
  selected: boolean
  drawing: boolean
  onSelect: () => void
  onChange: (patch: Partial<DeliveryZone>) => void
  onDelete: () => void
  onAddLibraryArea: (entry: AreaLibraryEntry) => void
  onRemovePolygon: (ringIdx: number) => void
  onDrawArea: () => void
  onPreviewArea: (entry: AreaLibraryEntry | null) => void
}

function ZoneCard({
  zone,
  selected,
  drawing,
  onSelect,
  onChange,
  onDelete,
  onAddLibraryArea,
  onRemovePolygon,
  onDrawArea,
  onPreviewArea,
}: ZoneCardProps) {
  const num = (v: string) => {
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 ? Math.min(n, 1_000_000) : 0
  }

  // Labels that aren't backed by a coverage ring (legacy free-text chips).
  const polygonNames = new Set(zone.polygons.map((p) => p.name.toLowerCase()))
  const labelOnly = zone.areas.filter((a) => !polygonNames.has(a.toLowerCase()))

  return (
    <Card
      className={`cursor-pointer transition-shadow ${selected ? 'ring-2' : ''}`}
      style={selected ? ({ ['--tw-ring-color' as string]: zone.color } as React.CSSProperties) : undefined}
      onClick={onSelect}
    >
      <CardContent className="space-y-3 p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="h-5 w-5 shrink-0 rounded-full border"
            style={{ backgroundColor: zone.color }}
            title="Change color"
            onClick={() => {
              const i = ZONE_COLORS.indexOf(zone.color as (typeof ZONE_COLORS)[number])
              onChange({ color: ZONE_COLORS[(i + 1) % ZONE_COLORS.length] })
            }}
          />
          <Input
            value={zone.name}
            onChange={(e) => onChange({ name: e.target.value.slice(0, 80) })}
            className="h-8 font-medium"
            onFocus={onSelect}
          />
          <Switch
            checked={zone.enabled}
            onCheckedChange={(v) => onChange({ enabled: v })}
            title={zone.enabled ? 'Zone active' : 'Zone disabled'}
          />
          <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs text-muted-foreground">Delivery charge (Rs)</Label>
            <Input
              type="number"
              min={0}
              value={zone.delivery_charge}
              onChange={(e) => onChange({ delivery_charge: num(e.target.value) })}
              className="h-8"
              onFocus={onSelect}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Minimum order (Rs)</Label>
            <Input
              type="number"
              min={0}
              value={zone.min_order}
              onChange={(e) => onChange({ min_order: num(e.target.value) })}
              className="h-8"
              onFocus={onSelect}
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Rider payout (Rs, internal)</Label>
            <Input
              type="number"
              min={0}
              value={zone.rider_charge}
              onChange={(e) => onChange({ rider_charge: num(e.target.value) })}
              className="h-8"
              onFocus={onSelect}
            />
          </div>
          <div>
            <Label
              className="text-xs text-muted-foreground"
              title="Travel time to this zone. Customers see this plus the longest prep time in their order."
            >
              Travel time (min)
            </Label>
            <Input
              type="number"
              min={0}
              max={1440}
              value={zone.eta_minutes}
              onChange={(e) => onChange({ eta_minutes: Math.min(num(e.target.value), 1440) })}
              className="h-8"
              onFocus={onSelect}
            />
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Customers are quoted travel time plus the longest prep time in their order.
        </p>

        <div>
          <Label className="text-xs text-muted-foreground">Coverage areas</Label>
          <div className="mt-1 flex flex-wrap items-center gap-1">
            {zone.polygons.map((p, i) => (
              <Badge key={`${p.slug || p.name}:${i}`} variant="secondary" className="gap-1">
                {p.source === 'library' ? <MapPinned className="h-3 w-3" /> : <Pencil className="h-3 w-3" />}
                {p.name}
                <button type="button" title="Remove area" onClick={() => onRemovePolygon(i)}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {labelOnly.map((a) => (
              <Badge key={a} variant="outline" className="gap-1" title="Label only — not used for matching">
                {a}
                <button type="button" onClick={() => onChange({ areas: zone.areas.filter((x) => x !== a) })}>
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            <AreaPicker zone={zone} onPick={onAddLibraryArea} onFocus={onSelect} onPreview={onPreviewArea} />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              disabled={drawing}
              title="Draw a custom area for this zone"
              onClick={onDrawArea}
            >
              <Pencil className="mr-1 h-3 w-3" /> Draw
            </Button>
          </div>
        </div>

        {zoneRings(zone).length === 0 && (
          <p className="text-xs text-destructive">This zone has no coverage area — it can never match.</p>
        )}
      </CardContent>
    </Card>
  )
}

// ── Area picker (predefined library combobox) ────────────────────────────────

type AreaPickerProps = {
  zone: DeliveryZone
  onPick: (entry: AreaLibraryEntry) => void
  onFocus: () => void
  /** Hovering an option previews its boundary on the map (null clears). */
  onPreview: (entry: AreaLibraryEntry | null) => void
}

function AreaPicker({ zone, onPick, onFocus, onPreview }: AreaPickerProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [lib, setLib] = useState<AreaLibrary | null>(null)
  const [libError, setLibError] = useState(false)

  useEffect(() => {
    if (!open || lib || libError) return
    loadAreaLibrary()
      .then(setLib)
      .catch(() => setLibError(true))
  }, [open, lib, libError])

  const inZone = useMemo(() => new Set(zone.polygons.map((p) => p.slug).filter(Boolean)), [zone.polygons])
  // Empty query = browse the whole list (grouped by city); typing filters it.
  const results = useMemo(() => (lib ? searchAreas(lib, query, query ? 15 : 100) : []), [lib, query])
  const grouped = useMemo(() => {
    const byCity = new Map<string, AreaLibraryEntry[]>()
    for (const entry of results) {
      const list = byCity.get(entry.city) ?? []
      list.push(entry)
      byCity.set(entry.city, list)
    }
    return [...byCity.entries()]
  }, [results])

  const close = () => {
    setOpen(false)
    onPreview(null)
  }

  // Plain inline dropdown — deliberately NOT a portal/popover component. The
  // first version used Radix Popover with the input as anchor; its
  // outside-click dismissal treated the very click that focused the input as
  // "outside" and closed the panel the instant it opened. A plain positioned
  // div under the input has no dismissal layer to race against.
  return (
    <div className="relative">
      <Input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value)
          if (!open) setOpen(true)
        }}
        onFocus={() => {
          onFocus()
          setOpen(true)
        }}
        onBlur={close}
        onKeyDown={(e) => {
          if (e.key === 'Escape') close()
        }}
        placeholder="Add area… e.g. F-10"
        className="h-7 w-36 text-xs"
      />
      {open && (
        <div
          className="absolute left-0 top-full z-50 mt-1 max-h-72 w-72 overflow-y-auto rounded-md border bg-popover p-1 text-popover-foreground shadow-md"
          // Options act on mousedown (before the input's blur) — preventDefault
          // keeps focus in the input so blur never fires mid-pick.
          onMouseDown={(e) => e.preventDefault()}
          onMouseLeave={() => onPreview(null)}
        >
          {libError && <p className="p-2 text-xs text-destructive">Could not load the area list.</p>}
          {!lib && !libError && (
            <p className="flex items-center gap-2 p-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading areas…
            </p>
          )}
          {lib && results.length === 0 && (
            <p className="p-2 text-xs text-muted-foreground">
              No predefined area matches "{query}" — use <strong>Draw</strong> to outline it by hand.
            </p>
          )}
          {grouped.map(([city, entries]) => (
            <div key={city}>
              <p className="px-2 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {city}
              </p>
              {entries.map((entry) => {
                const added = inZone.has(entry.slug)
                return (
                  <button
                    key={entry.slug}
                    type="button"
                    disabled={added}
                    className="flex w-full items-center justify-between rounded px-2 py-1.5 text-left text-xs hover:bg-accent disabled:opacity-50"
                    onMouseEnter={() => onPreview(entry)}
                    onClick={() => {
                      onPick(entry)
                      setQuery('')
                      close()
                    }}
                  >
                    <span>{entry.name}</span>
                    <span className="flex items-center gap-1 text-muted-foreground">
                      {entry.approx && <span title="Approximate boundary">≈</span>}
                      {added ? 'added' : <Plus className="h-3 w-3" />}
                    </span>
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
